// SPDX-License-Identifier: AGPL-3.0-or-later
import { useState } from "react";
import { addDays, isoDate, periodRange, yearOf, type IsoDate } from "@almanac/domain";
import { CategoryDonut } from "../components/CategoryDonut.js";
import { Donut } from "../components/Donut.js";
import { TrafficLights } from "../components/TrafficLights.js";
import { useApp } from "../lib/app.js";
import { agenda } from "../lib/events.js";
import { groupView, owedToMe } from "../lib/groups.js";
import { kpis, monthOutlook, needsCategory, plannedSlices, shiftMonth, spendingBreakdown, statuses } from "../lib/figures.js";
import { useI18n } from "../lib/i18n.js";
import { setPrefs, usePrefs } from "../lib/prefs.js";
import { useStore } from "../lib/store.js";

export function Dashboard() {
  const { store, today } = useApp();
  const state = useStore(store);
  const prefs = usePrefs();
  const { t, money, date } = useI18n();
  const thisMonth = shiftMonth(today, 0);
  const [month, setMonth] = useState<IsoDate>(thisMonth);
  const k = kpis(state.transactions, state.plan, today, state.events);
  const range = periodRange("month", month);
  const year = yearOf(month);
  const yearEnd = isoDate(year, 12, 31);
  const waiting = state.transactions.filter(needsCategory).length;
  const soon = agenda(state.events, today, addDays(today, 14)).slice(0, 5);
  const outlook = prefs.dashboardView === "outlook";
  // Money fronted for groups: what people owe you, and how many of them.
  const owed = state.groups.flatMap((g) => [...owedToMe(groupView(g, state.ledger, today))]);
  const owedTotal = owed.reduce((s, [, v]) => s + v, 0);

  const cards = [
    {
      label: t("kpi.cashAvailable"),
      value: k.leftThisMonth,
      sub:
        k.scheduled > 0
          ? t("kpi.ofBudgetScheduled", { budget: money(k.monthBudget), scheduled: money(k.scheduled) })
          : t("kpi.ofBudget", { budget: money(k.monthBudget) }),
      signed: true,
    },
    { label: t("kpi.spentToday"), value: k.spentToday, sub: t("kpi.dailyBudget", { budget: money(k.dailyBudget) }), signed: false },
    { label: t("kpi.monthToDate"), value: k.monthToDate, sub: t("kpi.ofBudget", { budget: money(k.monthBudget) }), signed: false },
    { label: t("kpi.netThisMonth"), value: k.netThisMonth, sub: "", signed: true },
  ];

  return (
    <>
      {waiting > 0 && (
        <p className="banner">
          {t("tx.waiting", { count: waiting })} <a href="#/transactions?review">{t("tx.review")}</a>
        </p>
      )}
      {owedTotal > 0 && (
        <p className="banner owed">
          {t("groups.dashOwed", { amount: money(owedTotal) })} <a href="#/groups">{t("groups.dashOpen")}</a>
        </p>
      )}
      <section aria-label={t("kpi.label")}>
        <ul className="grid dash-top" style={{ listStyle: "none", padding: 0, margin: 0 }}>
          {cards.map((c) => (
            <li className="card kpi" key={c.label}>
              <div className="label">{c.label}</div>
              <div className={`value ${c.signed ? (c.value < 0 ? "negative" : "positive") : ""}`}>{money(c.value)}</div>
              {c.sub && <div className="sub">{c.sub}</div>}
            </li>
          ))}
        </ul>
      </section>

      <div className="card period-bar" role="group" aria-label={t("dash.period")}>
        <div className="month-stepper">
          <button type="button" onClick={() => setMonth(shiftMonth(month, -1))} aria-label={t("dash.prevMonth")}>
            ‹
          </button>
          <span className="period-label" aria-live="polite">
            {date(month, "month")}
          </span>
          <button type="button" onClick={() => setMonth(shiftMonth(month, 1))} aria-label={t("dash.nextMonth")}>
            ›
          </button>
          {month !== thisMonth && (
            <button type="button" onClick={() => setMonth(thisMonth)}>
              {t("dash.thisMonth")}
            </button>
          )}
        </div>
        <fieldset>
          <legend className="visually-hidden">{t("dash.view")}</legend>
          <div className="choices">
            {(["month", "outlook"] as const).map((v) => (
              <label key={v}>
                <input type="radio" name="dashboard-view" checked={prefs.dashboardView === v} onChange={() => setPrefs({ dashboardView: v })} />
                {t(v === "month" ? "dash.view.month" : "dash.view.outlook")}
              </label>
            ))}
          </div>
        </fieldset>
      </div>

      <section className="card" aria-labelledby="big-title">
        <h2 id="big-title">{t("dash.bigPicture")}</h2>
        <div className={`big-picture ${outlook ? "two" : ""}`}>
          <CategoryDonut title={t("dash.today")} transactions={state.transactions} start={today} end={today} empty={t("dash.emptyDay")} />
          {!outlook && (
            <CategoryDonut
              key={month}
              title={date(month, "month")}
              transactions={state.transactions}
              start={range.start}
              end={range.end}
              empty={t("dash.emptyMonth", { month: date(month, "month") })}
            />
          )}
          <CategoryDonut
            key={year}
            title={yearEnd < today ? String(year) : t("dash.yearSoFar", { year })}
            transactions={state.transactions}
            start={isoDate(year, 1, 1)}
            end={yearEnd}
            empty={t("dash.emptyYear", { year })}
          />
        </div>
        <a href="#/year">{t("dash.wholeYear")}</a>
      </section>

      {outlook && (
        <section className="card" aria-labelledby="outlook-title" style={{ marginTop: "var(--gap)" }}>
          <h2 id="outlook-title">{t("dash.outlook")}</h2>
          <div className="outlook">
            {[-1, 0, 1].map((n) => {
              const m = shiftMonth(month, n);
              const o = monthOutlook(state.transactions, state.plan, state.events, m, today);
              const left = o.budget - o.spent - o.scheduled;
              return (
                <div key={m} className="outlook-month">
                  <h3>{date(m, "month")}</h3>
                  <dl className="figures">
                    <dt>{t("dash.budget")}</dt>
                    <dd>{money(o.budget)}</dd>
                    {o.when !== "future" && (
                      <>
                        <dt>{t("dash.spent")}</dt>
                        <dd>{money(o.spent)}</dd>
                      </>
                    )}
                    {o.when !== "past" && (
                      <>
                        <dt>{t("dash.scheduled")}</dt>
                        <dd>{money(o.scheduled)}</dd>
                      </>
                    )}
                    <dt>{left < 0 ? t("dash.over") : t("dash.left")}</dt>
                    <dd className={left < 0 ? "negative" : "positive"}>{money(Math.abs(left))}</dd>
                  </dl>
                  {o.when === "future" ? (
                    <>
                      <p className="hint">{t("dash.planned")}</p>
                      <Donut slices={plannedSlices(state.plan)} />
                    </>
                  ) : (
                    <Donut slices={spendingBreakdown(state.transactions, o.start, o.end)} empty={t("dash.emptyMonth", { month: date(m, "month") })} />
                  )}
                </div>
              );
            })}
          </div>
        </section>
      )}

      <div className="grid dash-mid">
        <section className="card" aria-labelledby="status-title">
          <h2 id="status-title">{t("status.title")}</h2>
          <TrafficLights statuses={statuses(state.transactions, state.plan, today)} />
        </section>
        <section className="card" aria-labelledby="soon-title">
          <h2 id="soon-title">{t("dash.comingUp")}</h2>
          {soon.length === 0 ? (
            <p className="hint">{t("dash.comingUpNone")}</p>
          ) : (
            <ul className="agenda">
              {soon.map((o) => (
                <li key={`${o.event.id}|${o.date}`}>
                  <span className="agenda-date">{date(o.date, "short")}</span>
                  <span className="agenda-title">{o.event.title}</span>
                  <span className="num">{o.event.amount ? money(o.event.amount) : ""}</span>
                </li>
              ))}
            </ul>
          )}
          <a href="#/events">{t("dash.allEvents")}</a>
        </section>
      </div>
    </>
  );
}
